import React, { useState, useEffect, useMemo, useCallback } from "react";
import { View, StyleSheet } from "react-native";
import { MessageComposer } from "../../components/chatNewInterface/chatLayout/messageComposer/MessageComposer";
import { AddTaskModal, type Todo } from "../../components/chatNewInterface/interfaceComponents/modals/AddTaskModal";
import {
  AddAssignmentModal,
  type Assignment,
} from "../../components/chatNewInterface/interfaceComponents/modals/AddAssignmentModal";
import useFileUpload, { type ProcessedFile } from "../../hooks/useFileUpload";
import {
  StoragePathType,
  AttachmentInput,
  AttachmentType,
  DocumentStatus,
  useGetDocumentQuery,
  useProcessCompanionChatAttachmentMutation,
} from "../../graphql/generated/graphql";

const generalSuggestions = [
  { text: "What's due this week?", color: "#e8ddfb", textColor: "#7244bb" },
  { text: "Help me study for my next quiz", color: "#d1e8eb", textColor: "#15727d" },
  { text: "When is the last day to withdraw?", color: "#cce6ff", textColor: "#0069cc" },
  { text: "Show me my upcoming assignments", color: "#fde8f0", textColor: "#c53469" },
];

const courseSuggestions = [
  { text: "What's due this week in this class?", color: "#e8ddfb", textColor: "#7244bb" },
  { text: "Explain this topic to me", color: "#d1e8eb", textColor: "#15727d" },
  { text: "Help me study for the next quiz", color: "#cce6ff", textColor: "#0069cc" },
  { text: "Summarize my latest assignment", color: "#fde8f0", textColor: "#c53469" },
];

const supportSuggestions = [
  { text: "When is the last day to withdraw?", color: "#e8ddfb", textColor: "#7244bb" },
  { text: "How do I apply for financial aid?", color: "#d1e8eb", textColor: "#15727d" },
  { text: "Where can I find tutoring services?", color: "#cce6ff", textColor: "#0069cc" },
  { text: "What are the add/drop deadlines?", color: "#fde8f0", textColor: "#c53469" },
];

interface ChatInputProps {
  value: string;
  onChangeText: (text: string) => void;
  onSubmit: () => void;
  disabled?: boolean;
  placeholder?: string;
  showSuggestions?: boolean;
  attachment: AttachmentInput | null;
  onAttachmentChange: (attachment: AttachmentInput | null) => void;
  activeWorkspaceType: "general" | "course" | "support";
}

const ChatInput = ({
  value,
  onChangeText,
  onSubmit,
  disabled = false,
  placeholder = "Type a message...",
  showSuggestions = true,
  attachment,
  onAttachmentChange,
  activeWorkspaceType = "general",
}: ChatInputProps) => {
  const [isTaskModalVisible, setIsTaskModalVisible] = useState(false);
  const [isAssignmentModalVisible, setIsAssignmentModalVisible] = useState(false);
  const [isFileProcessing, setIsFileProcessing] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string>("");

  const [selectedTask, setSelectedTask] = useState<Todo | null>(null);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);

  const [processCompanionChatAttachment] = useProcessCompanionChatAttachmentMutation();
  const isDocumentAttachment = attachment?.type === AttachmentType.Document;
  const documentId = isDocumentAttachment ? attachment.id : "";
  const {
    data: documentData,
    startPolling: startDocumentPolling,
    stopPolling: stopDocumentPolling,
  } = useGetDocumentQuery({
    variables: { id: documentId },
    skip: !isDocumentAttachment,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (!isDocumentAttachment || !isFileProcessing) {
      return;
    }
    startDocumentPolling?.(3000);
    return () => {
      stopDocumentPolling?.();
    };
  }, [isDocumentAttachment, isFileProcessing, startDocumentPolling, stopDocumentPolling]);

  // Check if file processing is complete
  useEffect(() => {
    if (!isDocumentAttachment) {
      return;
    }
    const document = documentData?.getDocument?.data;
    if (!document?.status) {
      return;
    }

    const firstFileName = document.files?.[0]?.fileName;
    if (firstFileName) {
      setUploadedFileName(firstFileName);
    }

    if (document.status === DocumentStatus.Completed) {
      setIsFileProcessing(false);
      stopDocumentPolling?.();
    } else if (document.status === DocumentStatus.Failed) {
      console.error("Document processing failed");
      setIsFileProcessing(false);
      stopDocumentPolling?.();
      onAttachmentChange(null);
      setUploadedFileName("");
    }
  }, [documentData, isDocumentAttachment, stopDocumentPolling, onAttachmentChange]);

  useEffect(() => {
    if (!attachment) {
      setSelectedTask(null);
      setSelectedAssignment(null);
      setIsFileProcessing(false);
      setUploadedFileName("");
      stopDocumentPolling?.();
    }
  }, [attachment, stopDocumentPolling]);

  const handleBeforeUpload = () => {
    setIsFileProcessing(true);
    setUploadedFileName("");
  };

  const handleAfterUpload = async (processedFilesArray: ProcessedFile[]) => {
    if (processedFilesArray.length === 0) return;

    const processedFile = processedFilesArray[0];
    const fileId = processedFile.file.id;
    const fileName = processedFile.file.fileName || "Document";

    try {
      setIsFileProcessing(true);
      setUploadedFileName(fileName);

      const processResponse = await processCompanionChatAttachment({
        variables: {
          fileId,
        },
      });

      const createdDocumentId = processResponse.data.processCompanionChatAttachment?.data?.documentId;
      if (!createdDocumentId) {
        throw new Error("Document creation failed");
      }

      onAttachmentChange({
        type: AttachmentType.Document,
        id: createdDocumentId,
        fileId,
      });
    } catch (error) {
      console.error("File processing error:", error);
      setIsFileProcessing(false);
      setUploadedFileName("");
      onAttachmentChange(null);
    }
  };

  const handleFileUploadError = (message?: string) => {
    console.error("File upload error:", message);
    setIsFileProcessing(false);
    setUploadedFileName("");
    stopDocumentPolling?.();
    onAttachmentChange(null);
  };

  const { handleFileUpload } = useFileUpload({
    storagePath: StoragePathType.ChatAttachments,
    beforeUpload: handleBeforeUpload,
    afterUpload: handleAfterUpload,
    onError: handleFileUploadError,
    multiple: false,
  });

  const handleAddFile = useCallback(async () => {
    try {
      const uploadedFiles = await handleFileUpload();
      if (!uploadedFiles || uploadedFiles.length === 0) {
        setIsFileProcessing(false);
      }
    } catch (error) {
      console.error("File upload failed:", error);
      setIsFileProcessing(false);
    }
  }, [handleFileUpload]);

  const suggestedPrompts = showSuggestions
    ? activeWorkspaceType === "general"
      ? generalSuggestions
      : activeWorkspaceType === "course"
        ? courseSuggestions
        : supportSuggestions
    : [];

  const handleAddTask = () => {
    setIsTaskModalVisible(true);
  };

  const handleSelectTask = (task: Todo) => {
    setSelectedTask(task);
    onAttachmentChange({
      type: AttachmentType.Todo,
      id: task.id,
    });
    setIsTaskModalVisible(false);
  };

  const handleAddAssignment = () => {
    setIsAssignmentModalVisible(true);
  };

  const handleSelectAssignment = (assignment: Assignment) => {
    setSelectedAssignment(assignment);
    onAttachmentChange({
      type: AttachmentType.Assignment,
      id: assignment.id,
    });
    setIsAssignmentModalVisible(false);
  };

  const handleRemoveAttachment = () => {
    onAttachmentChange(null);
    setSelectedTask(null);
    setSelectedAssignment(null);
    setIsFileProcessing(false);
    setUploadedFileName("");
    stopDocumentPolling?.();
  };

  const attachmentDisplay = useMemo(() => {
    if (!attachment) return null;

    switch (attachment.type) {
      case AttachmentType.Todo:
        return selectedTask
          ? {
              id: selectedTask.id,
              type: "task" as const,
              name: selectedTask.name,
              duedate: selectedTask.date,
              isLoading: false,
            }
          : null;

      case AttachmentType.Assignment:
        return selectedAssignment
          ? {
              id: selectedAssignment.id,
              type: "assignment" as const,
              name: selectedAssignment.title || "Untitled Assignment",
              duedate: selectedAssignment.due_date,
              isLoading: false,
            }
          : null;

      case AttachmentType.Document: {
        const document = documentData?.getDocument?.data;
        const file = document?.files?.[0];
        return {
          id: attachment.id,
          type: "file" as const,
          fileName: file?.fileName || uploadedFileName || "Document",
          isLoading: isFileProcessing,
        };
      }

      default:
        return null;
    }
  }, [attachment, selectedTask, selectedAssignment, documentData, uploadedFileName, isFileProcessing]);

  return (
    <View style={styles.container}>
      <MessageComposer
        disabled={disabled || isFileProcessing}
        message={value}
        setMessage={onChangeText}
        onSendMessage={onSubmit}
        placeholder={placeholder}
        suggestedPrompts={suggestedPrompts}
        addTask={handleAddTask}
        removeTask={handleRemoveAttachment}
        addAssignment={handleAddAssignment}
        removeAssignment={handleRemoveAttachment}
        addFile={handleAddFile}
        removeFile={handleRemoveAttachment}
        attachedTasks={attachmentDisplay?.type === "task" ? [attachmentDisplay] : []}
        attachedAssignments={attachmentDisplay?.type === "assignment" ? [attachmentDisplay] : []}
        attachedFiles={
          attachmentDisplay?.type === "file"
            ? [
                {
                  id: attachmentDisplay.id,
                  fileName: attachmentDisplay.fileName,
                  isLoading: attachmentDisplay.isLoading,
                },
              ]
            : []
        }
      />

      <AddTaskModal
        visible={isTaskModalVisible}
        onClose={() => setIsTaskModalVisible(false)}
        onSelectTask={handleSelectTask}
      />

      <AddAssignmentModal
        visible={isAssignmentModalVisible}
        onClose={() => setIsAssignmentModalVisible(false)}
        onSelectAssignment={handleSelectAssignment}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    paddingVertical: 12,
    paddingHorizontal: 20,
  },
});

export default ChatInput;
